import { db } from "@/src/lib/firebase";
import {
  collection,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  where,
  type DocumentSnapshot,
  type Query,
  type QueryDocumentSnapshot,
} from "firebase/firestore";
import { useCallback, useEffect, useRef, useState } from "react";

import { isLegacyPostForPersonalIdentity } from "../lib/identity";
import { toRealtimeLoadError, type RealtimeLoadError } from "@/src/lib/realtimeRecovery";
import type {
  AccountType,
  IdentityEntitlements,
  LegacyAccountType,
  PublicIdentitySnapshot,
  VerificationStatus,
} from "@/src/types/identity";

export type Post = {
  id: string;
  authorUid: string;
  authorIdentityId?: string;
  authorSnapshot?: PublicIdentitySnapshot;
  authorUsername: string;
  /** @deprecated Compatibility snapshot for historical student/teacher posts. */
  authorRole?: LegacyAccountType;
  authorAccountType?: AccountType;
  authorVerificationStatus?: VerificationStatus;
  authorEntitlements?: IdentityEntitlements;
  /** @deprecated Compatibility projection for legacy teacher posts. */
  teacherVerified?: boolean;
  text: string;
  subject: string;
  visibility: "global" | "group";
  groupId?: string | null;
  groupName?: string;
  groupIsPublic?: boolean;
  createdAt: any;
  likesCount: number;
  likedBy: string[];
  track?: string;
};

const PAGE_SIZE = 12;
const MODERATION_STATUSES = ["pending", "approved", "review", "rejected"] as const;

type IdentityPostSource = {
  key: string;
  baseQuery: Query;
  items: Post[];
  cursor: DocumentSnapshot | null;
  hasMore: boolean;
  settled: boolean;
  error: RealtimeLoadError | null;
  unsubscribe: (() => void) | null;
  includeLegacy: boolean;
};

function postFromSnapshot(snapshot: { id: string; data: () => unknown }) {
  return { id: snapshot.id, ...(snapshot.data() as any) } as Post;
}

function postMillis(post: Post) {
  return typeof post?.createdAt?.toMillis === "function" ? post.createdAt.toMillis() : 0;
}

function mergeIdentitySources(sources: readonly IdentityPostSource[], identityId: string) {
  const map = new Map<string, Post>();

  for (const source of sources) {
    for (const post of source.items) {
      if (source.includeLegacy) {
        if (!isLegacyPostForPersonalIdentity(post, identityId)) continue;
      } else if (post.authorIdentityId !== identityId) {
        continue;
      }
      map.set(post.id, post);
    }
  }

  return Array.from(map.values()).sort((left, right) => postMillis(right) - postMillis(left));
}

export function useMyPosts(uid: string | null, identityId?: string | null) {
  const [items, setItems] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<RealtimeLoadError | null>(null);
  const [retryKey, setRetryKey] = useState(0);

  const lastDocRef = useRef<DocumentSnapshot | null>(null);
  const unsubRef = useRef<null | (() => void)>(null);
  const identitySourcesRef = useRef<IdentityPostSource[]>([]);
  const previousUidRef = useRef<string | null>(null);
  const previousIdentityIdRef = useRef<string | null | undefined>(undefined);
  const previousModeRef = useRef<"legacy" | "identity" | null>(null);
  const loadingMoreRef = useRef(false);

  const selectedIdentityId = identityId === undefined ? uid : identityId;
  const mode = identityId === undefined ? "legacy" : "identity";

  const loadMore = useCallback(async () => {
    if (!uid || !selectedIdentityId || loadingMoreRef.current || !hasMore) return;

    if (mode === "identity") {
      const sources = [...identitySourcesRef.current];
      if (!sources.some((source) => source.hasMore && source.cursor)) return;

      try {
        loadingMoreRef.current = true;
        setLoadingMore(true);
        const failures: unknown[] = [];

        await Promise.all(sources.map(async (source) => {
          if (!source.hasMore || !source.cursor) return;
          try {
            const nextSnap = await getDocs(
              query(source.baseQuery, startAfter(source.cursor), limit(PAGE_SIZE))
            );
            source.items = [
              ...source.items,
              ...nextSnap.docs.map((snapshot) => postFromSnapshot(snapshot)),
            ];
            source.cursor = nextSnap.docs[nextSnap.docs.length - 1] ?? source.cursor;
            source.hasMore = nextSnap.size === PAGE_SIZE;
          } catch (nextError) {
            failures.push(nextError);
          }
        }));

        setItems(mergeIdentitySources(sources, selectedIdentityId));
        setHasMore(sources.some((source) => source.hasMore));
        if (failures.length > 0) {
          setError(toRealtimeLoadError(failures[0], "Impossible de charger tes posts."));
        }
      } finally {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
      return;
    }

    if (!lastDocRef.current) return;

    try {
      loadingMoreRef.current = true;
      setLoadingMore(true);
      const qMore = query(
        collection(db, "posts"),
        where("authorUid", "==", uid),
        where("moderationStatus", "in", MODERATION_STATUSES),
        orderBy("createdAt", "desc"),
        startAfter(lastDocRef.current as QueryDocumentSnapshot),
        limit(PAGE_SIZE)
      );

      const snap = await getDocs(qMore);
      const arr = snap.docs.map((snapshot) => postFromSnapshot(snapshot));

      setItems((prev) => {
        const map = new Map(prev.map((post) => [post.id, post]));
        for (const post of arr) map.set(post.id, post);
        return Array.from(map.values());
      });

      const last = snap.docs[snap.docs.length - 1] ?? null;
      lastDocRef.current = last;
      setHasMore(snap.size === PAGE_SIZE);
      setError(null);
    } catch (nextError) {
      if (__DEV__) console.warn("[MY_POSTS] load more error:", (nextError as any)?.code ?? "unknown");
      setError(toRealtimeLoadError(nextError, "Impossible de charger tes posts."));
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [hasMore, mode, selectedIdentityId, uid]);

  useEffect(() => {
    const selectionChanged = previousUidRef.current !== uid
      || previousIdentityIdRef.current !== selectedIdentityId
      || previousModeRef.current !== mode;
    previousUidRef.current = uid;
    previousIdentityIdRef.current = selectedIdentityId;
    previousModeRef.current = mode;

    // Clear pagination only when the controller or public identity changes. A
    // retry keeps the last valid page visible while the listener reconnects.
    if (selectionChanged) {
      setItems([]);
      setHasMore(true);
      lastDocRef.current = null;
    }
    setLoading(true);
    setLoadingMore(false);
    loadingMoreRef.current = false;
    setError(null);

    if (unsubRef.current) unsubRef.current();
    unsubRef.current = null;
    identitySourcesRef.current.forEach((source) => source.unsubscribe?.());
    identitySourcesRef.current = [];

    if (!uid || !selectedIdentityId) {
      setLoading(false);
      return;
    }

    if (mode === "identity") {
      const sources: IdentityPostSource[] = [
        {
          key: "identity",
          baseQuery: query(
            collection(db, "posts"),
            where("authorUid", "==", uid),
            where("authorIdentityId", "==", selectedIdentityId),
            where("moderationStatus", "in", MODERATION_STATUSES),
            orderBy("createdAt", "desc")
          ),
          items: [],
          cursor: null,
          hasMore: true,
          settled: false,
          error: null,
          unsubscribe: null,
          includeLegacy: false,
        },
      ];

      // C4 posts are identity-addressed. The second source keeps historical
      // personal posts visible without leaking institution posts into them.
      if (selectedIdentityId === uid) {
        sources.push({
          key: "legacy-personal",
          baseQuery: query(
            collection(db, "posts"),
            where("authorUid", "==", uid),
            where("moderationStatus", "in", MODERATION_STATUSES),
            orderBy("createdAt", "desc")
          ),
          items: [],
          cursor: null,
          hasMore: true,
          settled: false,
          error: null,
          unsubscribe: null,
          includeLegacy: true,
        });
      }

      identitySourcesRef.current = sources;
      let active = true;

      const sync = () => {
        if (!active) return;
        setItems(mergeIdentitySources(sources, selectedIdentityId));
        setHasMore(sources.some((source) => source.hasMore));
        const firstError = sources.find((source) => source.error)?.error ?? null;
        setError(firstError);
        if (sources.every((source) => source.settled)) setLoading(false);
      };

      sources.forEach((source) => {
        const unsubscribe = onSnapshot(
          query(source.baseQuery, limit(PAGE_SIZE)),
          (snap) => {
            if (!active) return;
            source.items = snap.docs
              .map((snapshot) => postFromSnapshot(snapshot))
              .filter((post) => source.includeLegacy
                ? !post.authorIdentityId || post.authorIdentityId === selectedIdentityId
                : post.authorIdentityId === selectedIdentityId);
            source.cursor = snap.docs[snap.docs.length - 1] ?? null;
            source.hasMore = snap.size === PAGE_SIZE;
            source.settled = true;
            source.error = null;
            sync();
          },
          (nextError) => {
            if (!active) return;
            source.settled = true;
            source.hasMore = false;
            source.error = toRealtimeLoadError(nextError, "Impossible de charger tes posts.");
            sync();
          }
        );
        source.unsubscribe = unsubscribe;
      });

      return () => {
        active = false;
        sources.forEach((source) => source.unsubscribe?.());
        if (identitySourcesRef.current === sources) identitySourcesRef.current = [];
      };
    }

    const q = query(
      collection(db, "posts"),
      where("authorUid", "==", uid),
      where("moderationStatus", "in", MODERATION_STATUSES),
      orderBy("createdAt", "desc"),
      limit(PAGE_SIZE)
    );
    let active = true;

    unsubRef.current = onSnapshot(
      q,
      (snap) => {
        if (!active) return;
        const arr = snap.docs.map((snapshot) => postFromSnapshot(snapshot));
        setItems(arr);
        setError(null);

        const last = snap.docs[snap.docs.length - 1] ?? null;
        lastDocRef.current = last;
        setHasMore(snap.size === PAGE_SIZE);
        setLoading(false);
      },
      (nextError) => {
        if (!active) return;
        active = false;
        if (__DEV__) console.warn("[MY_POSTS] snapshot error:", (nextError as any)?.code ?? "unknown");
        setError(toRealtimeLoadError(nextError, "Impossible de charger tes posts."));
        setLoading(false);
      }
    );

    return () => {
      active = false;
      if (unsubRef.current) unsubRef.current();
      unsubRef.current = null;
    };
  }, [mode, retryKey, selectedIdentityId, uid]);

  const retry = useCallback(() => {
    setError(null);
    setLoading(true);
    setRetryKey((key) => key + 1);
  }, []);

  return { items, loading, loadMore, loadingMore, hasMore, error, retry };
}
